import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import SectionActions, { SECTION_BUTTON } from "../components/settings/SectionActions.jsx";
import DataTable from "../components/DataTable.jsx";
import OverlayDialog from "../components/OverlayDialog.jsx";
import { EndpointAssignmentEditor } from "../components/endpoints/EndpointAssignmentsSection.jsx";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import Button from "../components/ui/Button.jsx";
import Listing from "../components/ui/Listing.jsx";
import SearchInput from "../components/ui/SearchInput.jsx";
import { Count } from "../components/ui/TableBar.jsx";
import { COLUMN_SORT_FIELD, renderInventoryCell } from "../components/endpoints/inventoryTable.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { ENTITLEMENTS, hasEntitlement } from "../lib/entitlements.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";
import { allowsGate, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { restGet } from "../lib/restClient.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";

// On a narrow screen the columns that matter least fold away first
// (`fold`, 1 first): agent version, then operating system, then last
// reported. Hostname stretches.
const COLUMNS = [
    { id: "hostname", label: "Hostname", width: 240, flex: true, minWidth: 180 },
    { id: "platform", label: "Operating system", width: 150, fold: 2 },
    { id: "agentVersion", label: "Agent version", width: 130, fold: 1 },
    { id: "snapshotTime", label: "Last reported", width: 170, fold: 3 },
    { id: "assignment", label: "Assignment", width: 150, sortable: false },
];

export default function UnassignedEndpointsPage({ entitlements = null }) {
    const caps = useMyPermissions();
    const identity = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    // Settings gates this section the same way (lib/settingsModel.js); the
    // page holds the line itself too, so a direct mount can't skip it.
    if (!hasEntitlement(entitlements, ENTITLEMENTS.ABAC)
        || !allowsGate({ confirmed: PERMISSIONS.USERS_MANAGE }, caps)) return null;
    return <UnassignedInventory key={identity} />;
}

function UnassignedInventory() {
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
    const [refresh, setRefresh] = useState(0);
    const [selected, setSelected] = useState(null);
    const [sortBy, setSortBy] = useState({ id: "snapshotTime", dir: "desc" });
    const onSortChange = useCallback(id => setSortBy(current => ({
        id, dir: current.id === id && current.dir === "asc" ? "desc" : "asc",
    })), []);
    const changed = useCallback(() => setRefresh(value => value + 1), []);
    return <>
        <SectionActions>
            <button type="button" className={SECTION_BUTTON} onClick={changed}>Refresh</button>
        </SectionActions>
        <InventoryPage search={debouncedSearch} refresh={refresh}
            searchField={<SearchInput value={search} maxLength={200} onChange={setSearch} size="sm"
                label="Search hostname" placeholder="Search by hostname" className="w-[260px] max-w-full" />}
            sortBy={sortBy} onSortChange={onSortChange} onSelect={setSelected} />
        {selected && <OverlayDialog open onClose={() => setSelected(null)} title={selected.hostname || "Endpoint assignment"}
            eyebrow="Directory user assignment" analyticsId={DIALOG.ENDPOINT_ASSIGNMENT}>
            <EndpointAssignmentEditor endpointId={selected.id} endpointLabel={selected.hostname} onChanged={changed} />
        </OverlayDialog>}
    </>;
}

function InventoryPage({ search, searchField, refresh, sortBy, onSortChange, onSelect }) {
    const [cursor, setCursor] = useState({ id: "", value: "" });
    const [previous, setPrevious] = useState([]);
    // A new query (search, sort, Refresh) starts again from the first page.
    // Reset here rather than by remounting, so the last page stays on screen
    // (dimmed by DataTable) until the new one lands.
    const query = `${search}:${refresh}:${sortBy.id}:${sortBy.dir}`;
    const [pagedQuery, setPagedQuery] = useState(query);
    if (pagedQuery !== query) {
        setPagedQuery(query);
        setCursor({ id: "", value: "" });
        setPrevious([]);
    }
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [retry, setRetry] = useState(0);
    const cols = useDataTableColumns({ storageKey: "unassignedEndpoints", columns: COLUMNS, defaultVisible: COLUMNS.map(column => column.id) });
    useEffect(() => {
        let current = true;
        setLoading(true);
        setError("");
        restGet("/v1/endpoints/unassigned", {
            page_size: 50, after_endpoint_id: cursor.id, after_sort_value: cursor.value, search,
            sort_field: COLUMN_SORT_FIELD[sortBy.id], sort_desc: sortBy.dir === "desc",
        })
            .then(value => { if (current) setResult(value); })
            .catch(() => {
                if (!current) return;
                // Drop the previous page: it answered a different query.
                setResult(null);
                setError("Could not load unassigned endpoints. Retry or refresh the page.");
            })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; };
    }, [cursor, search, retry, sortBy, refresh]);
    const rows = (result?.endpoints ?? []).map(endpoint => ({
        id: endpoint.endpointId ?? endpoint.endpoint_id,
        hostname: endpoint.hostname,
        platform: endpoint.operatingSystem ?? endpoint.operating_system,
        agentVersion: endpoint.agentVersion ?? endpoint.agent_version,
        snapshotTime: endpoint.lastSeen || endpoint.last_seen ? new Date(endpoint.lastSeen ?? endpoint.last_seen) : null,
    }));
    const next = result?.nextAfterEndpointId ?? result?.next_after_endpoint_id ?? "";
    const nextValue = result?.nextAfterSortValue ?? result?.next_after_sort_value ?? "";
    const count = loading && !result ? null : rows.length;
    // An empty page that isn't the only one (a search, or a Next or Previous
    // to go to) keeps the bar, so the paging and the search stay reachable.
    const paged = Boolean(next) || previous.length > 0;
    return <div className="flex min-w-0 flex-1 flex-col" aria-busy={loading}>
        <Listing
            className="flex-1"
            count={count}
            error={error}
            onRetry={() => setRetry(value => value + 1)}
            filtered={Boolean(search) || paged}
            bar={<>
                {searchField}
                <Count n={count} one="endpoint shown" many="endpoints shown" />
            </>}
            barEnd={<>
                <Button size="sm" disabled={loading || !previous.length} onClick={() => {
                    setCursor(previous.at(-1)); setPrevious(values => values.slice(0, -1));
                }}>Previous</Button>
                <Button size="sm" disabled={loading || !next} onClick={() => {
                    setPrevious(values => [...values, cursor]); setCursor({ id: next, value: nextValue });
                }}>Next</Button>
            </>}
            empty={{ title: "No unassigned endpoints", note: "There are no endpoints awaiting a directory-user assignment." }}
            noMatch={next ? "No assignable endpoints on this page. Select Next to continue."
                : search ? "No matching unassigned endpoints. Try a different hostname."
                : "No unassigned endpoints on this page. Select Previous to go back."}
        >
            <DataTable cols={cols} rows={rows} sortBy={sortBy} onSortChange={onSortChange} getRowId={row => row.id} onRowClick={onSelect}
                renderCell={(row, column) => column.id === "assignment"
                    ? <span className="text-[12px] text-[color:var(--ink)] underline underline-offset-2">Manage assignment</span>
                    : renderInventoryCell(row, column)}
                loading={loading} skeletonRows={6} />
        </Listing>
    </div>;
}
